import { useEffect, useState } from "react";

import {
    ContactDetailsDto,
    MenuSettingsDto,
    WorkingScheduleDayDto,
    WorkingScheduleDto,
} from "services/menu/restModels";
import { parseTimeString } from "shared/lib/timeSlot";

import { ContactField, WeekDayKey } from "features/MenuSettings/types";

const EMPTY_CONTACTS: ContactDetailsDto = {
    phoneNumber: null,
    websiteUrl: null,
    whatsApp: null,
    instagram: null,
    telegram: null,
};

// День без слотов приходит от заведений, которые никогда не задавали время.
const DEFAULT_SLOT = { fromHour: 8, fromMinute: 0, toHour: 23, toMinute: 0 };

export const useGeneralInfoState = (settings: MenuSettingsDto | null) => {
    const [name, setName] = useState<Record<string, string>>({});
    const [description, setDescription] = useState<Record<string, string>>({});
    const [region, setRegion] = useState<Record<string, string>>({});
    const [addressTitle, setAddressTitle] = useState<Record<string, string>>({});
    // Ссылку на карты форма не редактирует — её никто не показывает. Держим ради
    // круглого рейса: сохранение не должно затирать значение, если оно вдруг есть.
    const [mapsUrl, setMapsUrl] = useState<string | null>(null);
    const [contacts, setContacts] = useState<ContactDetailsDto>(EMPTY_CONTACTS);
    const [workingSchedule, setWorkingSchedule] = useState<WorkingScheduleDto | null>(null);

    useEffect(() => {
        if (!settings) return;

        setName(settings.name);
        setDescription(settings.description);
        setRegion(settings.addressDetails.region);
        setAddressTitle(settings.addressDetails.title);
        setMapsUrl(settings.addressDetails.mapsUrl);
        setContacts(settings.contactDetails);
        setWorkingSchedule(settings.workingSchedule);
    }, [settings]);

    const setLocalizedName = (value: string, lang: string) =>
        setName((prev) => ({ ...prev, [lang]: value }));

    const setLocalizedDescription = (value: string, lang: string) =>
        setDescription((prev) => ({ ...prev, [lang]: value }));

    const setLocalizedRegion = (value: string, lang: string) =>
        setRegion((prev) => ({ ...prev, [lang]: value }));

    const setLocalizedAddressTitle = (value: string, lang: string) =>
        setAddressTitle((prev) => ({ ...prev, [lang]: value }));

    const setContactField = (field: ContactField, value: string) =>
        setContacts((prev) => ({ ...prev, [field]: value }));

    const setTimezone = (timezoneInMinutes: number) =>
        setWorkingSchedule((prev) => (prev ? { ...prev, timezoneInMinutes } : prev));

    const updateDay = (
        day: WeekDayKey,
        updater: (current: WorkingScheduleDayDto) => WorkingScheduleDayDto
    ) =>
        setWorkingSchedule(
            (prev) => (prev ? ({ ...prev, [day]: updater(prev[day]) } as WorkingScheduleDto) : prev)
        );

    const setDayOff = (day: WeekDayKey, isDayOff: boolean) =>
        updateDay(day, (current) => ({ ...current, isDayOff }));

    /*
    * Форма показывает один интервал на день — как и старая. Слоты дальше первого
    * задаются не отсюда, поэтому переносим их без изменений.
    * */
    const setDayTime = (day: WeekDayKey, edge: "from" | "to", value: string) => {
        const parsed = parseTimeString(value);
        if (!parsed) return;

        updateDay(day, (current) => {
            const slots = current.slots.length > 0 ? current.slots : [DEFAULT_SLOT];
            const first = slots[0];
            const updatedFirst =
                edge === "from"
                    ? { ...first, fromHour: parsed.hour, fromMinute: parsed.minute }
                    : { ...first, toHour: parsed.hour, toMinute: parsed.minute };

            return { ...current, slots: [updatedFirst, ...slots.slice(1)] };
        });
    };

    const getDaySlot = (day: WeekDayKey) => {
        if (!workingSchedule) return DEFAULT_SLOT;
        const slots = workingSchedule[day].slots;
        return slots.length > 0 ? slots[0] : DEFAULT_SLOT;
    };

    return {
        name,
        description,
        region,
        addressTitle,
        mapsUrl,
        contacts,
        workingSchedule,
        setLocalizedName,
        setLocalizedDescription,
        setLocalizedRegion,
        setLocalizedAddressTitle,
        setContactField,
        setTimezone,
        setDayOff,
        setDayTime,
        getDaySlot,
    };
};

export type GeneralInfoState = ReturnType<typeof useGeneralInfoState>;
